<template>
  <div>
    <transition name="fast-fade">
      <div class="property-modal" :class="{ 'has-features': showFeatures }" v-if="property">
        <div class="property-modal__mask" @click.stop="closeModal"></div>

        <div class="property-modal__inner">
          <div>
            <div v-if="config.general?.showOnHoldGraphic && property.status === 'on-hold'" class="property-modal__status" :class="`property-modal__status--${property.status}`"></div>
            <div v-if="config.general?.showArtistImpressionText" class="property-modal__note" :class="`property-modal__note--${config.general?.artistImpressionPosition}`">Artist impression only</div>
            <div class="property-modal__gallery">
              <img class="property-modal__overlay-image" v-if="overlayImageUrl && imageIndex === 0" :src="overlayImageUrl" />

              <img v-if="activeImage" :src="`${activeImage}`" :key="activeImage" :class="{ touch: imageIndex === images.length - 1 }" @click="openVirtualTour" />

              <video v-if="activeVideo" :src="`${activeVideo}`" loop muted autoplay disablePictureInPicture poster="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" />

              <div v-if="images.length > 1" class="property-modal__swipe-layer" @touchstart="handleTouchStart($event)" @touchmove="handleTouchMove($event)" @touchend="handleTouchEnd($event)" @touchcancel="resetTouchState"></div>

              <div class="property-modal__controls">
                <div v-if="images.length > 1" class="prev">
                  <button class="btn-circle touch nav-button" :class="{ inactive: imageIndex <= 0 }" @click="goToPrevImage">
                    <div class="nav-button__ring"></div>
                    <fa :icon="['far', 'chevron-left']" />
                  </button>
                </div>

                <div v-if="images.length > 1" class="next">
                  <button class="btn-circle touch nav-button" :class="{ inactive: imageIndex >= images.length - 1 }" @click="goToNextImage">
                    <div class="nav-button__ring"></div>
                    <fa :icon="['far', 'chevron-right']" />
                  </button>
                </div>
              </div>

              <div v-if="images.length > 1" class="property-modal__dots">
                <button v-for="(image, index) in images" :key="`modal-dot-${index}`" class="property-modal__dot touch" :class="{ 'property-modal__dot--active': imageIndex === index }" @click="imageIndex = index"></button>
              </div>
            </div>
          </div>

          <div class="property-modal__info">
            <div class="detail-wrapper" :dir="language.dir">
              <div v-if="isDisplayHome" class="details">
                <div v-if="property.dwelling && (property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel['en'])" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBuilder[language.code] || config.labels?.dwellingBuilder["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel["en"] }}</div>
                </div>
                <div class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingDesign[language.code] || config.labels?.dwellingDesign["en"] }}</div>
                  <div class="detail__value">{{ property.displayLabel }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingSize[language.code] || config.labels?.dwellingSize["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>
                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBeds[language.code] || config.labels?.dwellingBeds["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBaths[language.code] || config.labels?.dwellingBaths["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingCars[language.code] || config.labels?.dwellingCars["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>
              </div>

              <div v-else-if="isTurnkey" class="details">
                <div v-if="property.dwelling && (property.dwelling.displayName[language.code] || property.dwelling.displayName['en'])" class="detail">
                  <div class="detail__label">Name</div>
                  <div class="detail__value">{{ property.dwelling.displayName[language.code] || property.dwelling.displayName["en"] }}</div>
                </div>

                <div class="detail">
                  <div class="detail__label">{{ config.labels?.propertyLabel[language.code] || config.labels?.propertyLabel["en"] }}</div>
                  <div class="detail__value">{{ property.label }}</div>
                </div>

                <div v-if="property.dwelling && (property.dwelling.houseDesign[language.code] || property.dwelling.houseDesign['en'])" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingDesign[language.code] || config.labels?.dwellingDesign["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.houseDesign[language.code] || property.dwelling.houseDesign["en"] }}</div>
                </div>

                <div v-if="property.dwelling && (property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel['en'])" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBuilder[language.code] || config.labels?.dwellingBuilder["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel["en"] }}</div>
                </div>

                <div v-if="property && property.frontage" class="detail">
                  <div class="detail__label">Frontage</div>
                  <div class="detail__value" dir="ltr">{{ property.frontage }}m</div>
                </div>

                <div v-if="property && property.landSize" class="detail">
                  <div class="detail__label">{{ config.labels?.propertySize[language.code] || config.labels?.propertySize["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.landSize }}m<sup>2</sup></div>
                </div>

                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingSize[language.code] || config.labels?.dwellingSize["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>

                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBeds[language.code] || config.labels?.dwellingBeds["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBaths[language.code] || config.labels?.dwellingBaths["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingCars[language.code] || config.labels?.dwellingCars["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>

                <div v-if="config?.general.showPrice && property.price && (property.status !== 'sold' || (property.status === 'sold' && config.general.showSoldPrice))" class="detail">
                  <div class="detail__label">{{ config.labels?.propertyPrice[language.code] || config.labels?.propertyPrice["en"] }}</div>
                  <div class="detail__value">{{ property.price }}</div>
                </div>
              </div>

              <div v-else class="details">
                <div v-if="isHouse && property.dwelling && (property.dwelling.displayName[language.code] || property.dwelling.displayName.en)" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingLabel[language.code] || config.labels?.dwellingLabel["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.displayName[language.code] || property.dwelling.displayName.en }}</div>
                </div>
                <div v-else class="detail">
                  <div class="detail__label">{{ config.labels?.propertyLabel[language.code] }}</div>
                  <div class="detail__value">{{ property.label }}</div>
                </div>
                <div v-if="property.dwelling && (property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel['en'])" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBuilder[language.code] || config.labels?.dwellingBuilder["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.builderLabel[language.code] || property.dwelling.builderLabel["en"] }}</div>
                </div>
                <div v-if="false && property.dwelling && (property.dwelling.houseDesign[language.code] || property.dwelling.houseDesign['en'])" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingDesign[language.code] || config.labels?.dwellingDesign["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.houseDesign[language.code] || property.dwelling.houseDesign["en"] }}</div>
                </div>
                <div v-if="false && property.dwelling && property.dwelling.label" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingLabel[language.code] || config.labels?.dwellingLabel["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.label }}</div>
                </div>

                <div v-if="property && property.frontage" class="detail detail--frontage">
                  <div class="detail__label">{{ config.labels?.frontage[language.code] || config.labels?.frontage["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.frontage }}m</div>
                </div>

                <div v-if="property && property.depth" class="detail detail--depth">
                  <div class="detail__label">{{ config.labels?.depth[language.code] || config.labels?.depth["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.depth }}m</div>
                </div>

                <div v-if="!property.isDisplayHome && property.landSize" class="detail">
                  <div class="detail__label">{{ config.labels?.propertySize[language.code] || config.labels?.propertySize["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.landSize }}m<sup>2</sup></div>
                </div>

                <div v-if="property.dwelling && property.dwelling.size" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingSize[language.code] || config.labels?.dwellingSize["en"] }}</div>
                  <div class="detail__value" dir="ltr">{{ property.dwelling.size }}m<sup>2</sup></div>
                </div>

                <div v-if="property && property.viewLabel && (property.viewLabel[language.code] || property.viewLabel.en)" class="detail">
                  <div class="detail__label">{{ config.labels?.viewLabel[language.code] || config.labels?.viewLabel.en }}</div>
                  <div class="detail__value" dir="ltr">{{ property.viewLabel[language.code] }}</div>
                </div>

                <div v-if="property.dwelling && property.dwelling.beds" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBeds[language.code] || config.labels?.dwellingBeds["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.beds }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.baths" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingBaths[language.code] || config.labels?.dwellingBaths["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.baths }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.cars" class="detail">
                  <div class="detail__label">{{ config.labels?.dwellingCars[language.code] || config.labels?.dwellingCars["en"] }}</div>
                  <div class="detail__value">{{ property.dwelling.cars }}</div>
                </div>
                <div v-if="property.dwelling && property.dwelling.minimumFrontage" class="detail">
                  <div class="detail__label">Min. Frontage</div>
                  <div class="detail__value">{{ property.dwelling.minimumFrontage }}m</div>
                </div>
                <div v-if="config.general?.showPrice && property.price && (property.status !== 'sold' || (property.status === 'sold' && config.general.showSoldPrice))" class="detail">
                  <div class="detail__label">{{ hasDefaultPropertyLabel ? config.labels?.propertyPrice[language.code] || config.labels?.propertyPrice["en"] : property.priceLabel }}</div>
                  <div class="detail__value">{{ property.price }}</div>
                </div>
              </div>
              <div class="spacer"></div>
              <div class="property-modal__buttons">
                <button v-for="iframe in property.iframes" class="touch btn btn--auto" :key="iframe.label" @click="$store.commit('actions/SET_IFRAME_URL', iframe.url)">
                  <!-- <div v-if="iframe.icon" class="btn__icon"><fa :icon="['fas', iframe.icon]"/></div> -->
                  <div>{{ iframe.label }}</div>
                </button>

                <div v-if="property.associatedPackages && property.associatedPackages.length" class="associated-packages">
                  <button class="touch btn btn--auto" @click="viewPackages">
                    <!-- <div class="btn__icon"><fa :icon="['fas', 'home']"/></div> -->
                    <div>{{ config.propertyModal?.associatedPackagesButtonLabel || "View Packages" }}</div>
                  </button>
                </div>

                <div v-if="property.associatedLotPath" class="associated-packages">
                  <router-link class="touch btn btn--auto" :to="property.associatedLotPath">
                    <!-- <div class="btn__icon"><fa :icon="['fas', 'map-marked-alt']"/></div> -->
                    <div>View Lot</div>
                  </router-link>
                </div>

                <div v-if="config.propertyModal?.showSuitableDwellingsButton && property.suitableDwellings && property.suitableDwellings.length" class="associated-packages">
                  <router-link class="touch btn btn--auto" :to="`${buildDwellingsPath($route)}?lotId=${property._id}`">
                    <!-- <div class="btn__icon"><fa :icon="['fas', 'home']"/></div> -->
                    <div>{{ config.propertyModal?.suitableDwellingsButtonLabel }}</div>
                  </router-link>
                </div>

                <div v-if="facadeImages.length > 1" class="facade">
                  <div>
                    <button class="touch btn btn--auto" @click="viewFacades">
                      <span>View Facades</span>
                    </button>
                  </div>
                </div>

                <template v-if="isAddToFavouritesVisible">
                  <button v-if="!isFavourited(property._id)" class="btn btn--favourites touch" @click="addToFavourites(property)">
                    <fa class="icon" :icon="['far', 'heart']" />
                    <div>{{ config.labels.addToFavourites[language.code] || config.labels.addToFavourites.en }}</div>
                  </button>

                  <button v-if="isFavourited(property._id)" class="btn btn--favourites-remove touch" @click="removeFromFavourites(property)">
                    <fa class="icon" :icon="['far', 'trash']" />
                    <div>Remove</div>
                  </button>
                </template>
              </div>

              <div class="close">
                <button class="btn-circle touch" @click="closeModal">
                  <fa :icon="['far', 'times']" />
                </button>
              </div>
            </div>
          </div>

          <div v-if="showFeatures" class="property-modal__features" :dir="language.dir">
            <div v-if="property.description?.[language.code] || property.description?.['en']" class="feature-heading">{{ config.labels.description[language.code] || config.labels.description["en"] }}</div>
            <div v-if="property.description?.[language.code] || property.description?.['en']" class="feature" v-html="property.description[language.code] || property.description['en']" />
            <div v-if="property.dwelling?.featureList?.[language.code] || property.dwelling?.featureList?.en" class="feature-heading">{{ config.labels.features[language.code] || config.labels.features["en"] }}</div>
            <div v-if="property.dwelling?.featureList?.[language.code] || property.dwelling?.featureList?.en" class="feature-list" v-html="property.dwelling?.featureList[language.code] || property.dwelling?.featureList['en']" />
            <div v-if="property.isDisplayHome && property.displayHomeHours" class="feature-heading">{{ config.labels.openingHours[language.code] || config.labels.openingHours["en"] }}</div>
            <div v-if="property.isDisplayHome && property.displayHomeHours" class="feature" v-html="property.displayHomeHours.en" />
            <div v-if="property.dwelling">
              <div v-if="property.dwelling.builderDescription[language.code] || property.dwelling.builderDescription['en'] || property.dwelling.builderImage" class="feature-heading">{{ config.labels.dwellingBuilder[language.code] || config.labels.dwellingBuilder["en"] }}</div>
              <img v-if="property.dwelling.builderImage" class="property-modal__builder-logo" :src="$store.state.assetsPath + property.dwelling.builderImage" />
              <div v-if="property.dwelling.builderDescription[language.code] || property.dwelling.builderDescription['en']" class="feature" v-html="property.dwelling.builderDescription[language.code] || property.dwelling.builderDescription['en']"></div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import isEmpty from "lodash/isEmpty";
import { buildCarouselBasePath, buildDwellingsPath, buildPackagesPath, buildProductDisplayHomesPath, buildProductPackagesPath, isPropertyRoute } from "@/utils/livingRoute";

export default {
  props: ["property"],
  data() {
    return {
      imageIndex: 0,
      images: [],
      touchStartX: 0,
      touchStartY: 0,
      touchDeltaX: 0,
      touchDeltaY: 0,
      swipeThreshold: 50,
      swipeVerticalTolerance: 60,
      didSwipe: false,
    };
  },

  computed: {
    ...mapGetters({
      activeCategory: "activeCategory",
      language: "language",
      packages: "property/livingOptionsPackages",
      dwellings: "property/livingOptionsDwellings",
      properties: "property/properties",
      activeProperty: "property/activeProperty",
      config: "newConfig",
    }),

    activeSubsectionId() {
      return this.$store.state.route.query.activeSubsectionId;
    },

    activeImage() {
      const activeAsset = this.images.length ? this.images[this.imageIndex] : "";
      const ext = activeAsset && activeAsset.split(".").pop().toLowerCase();
      if (ext !== "mp4" && ext !== "mov") {
        return activeAsset;
      } else {
        return null;
      }
    },

    activeVideo() {
      const activeAsset = this.images.length ? this.images[this.imageIndex] : "";
      const ext = activeAsset && activeAsset.split(".").pop().toLowerCase();
      if (ext === "mp4" || ext === "mov") {
        return activeAsset;
      } else {
        return null;
      }
    },

    env() {
      return process.env;
    },

    favouriteIds() {
      return this.$store.state.favourites.favouriteIds;
    },

    activePackage() {
      if (this.isPackage) {
        const packageId = this.$store.state.route.params.packageId;

        return this.packages.find((_package) => {
          return _package._id === packageId;
        });
      }

      return {};
    },

    isAddToFavouritesVisible() {
      if (!this.config.general.showFavourites) return false;
      if (this.config.general.canAddSoldPropertiesToFavourites) return true;

      return this.property.status !== "sold";
    },

    isDisplayHome() {
      return this.property.type === "display-home";
    },

    isTurnkey() {
      return this.property.type === "turnkey";
    },

    isHouse() {
      return this.$store.state.route.name === "dwelling-modal";
    },

    isPackage() {
      return this.$store.state.route.name === "package-modal";
    },

    activeDwelling() {
      if (this.isHouse) {
        const dwellingId = this.$store.state.route.params.dwellingId;

        return this.dwellings.find((dwelling) => {
          return dwelling._id === dwellingId;
        });
      }

      return null;
    },

    facadeImages() {
      if (this.isHouse) {
        return this.activeDwelling.facadeImages;
      }
      if (this.isPackage) {
        return this.activePackage.dwelling.facadeImages;
      }

      return [];
    },

    overlayImageUrl() {
      if (!this.property.dwelling || !this.property.dwelling.overlayImage) return false;
      return this.$store.state.assetsPath + this.property.dwelling.overlayImage;
    },

    showFeatures() {
      return this.property.description[this.language.code] || (this.property.dwelling && this.property.dwelling.featureList[this.language.code]) || (this.property.dwelling && this.property.dwelling.builderDescription[this.language.code]) || (this.property.isDisplayHome && this.property.displayHomeHours[this.language.code]);
    },

    virtualTourUrl() {
      if (this.activePackage.virtualTourUrl) {
        return this.activePackage.virtualTourUrl;
      }

      if (this.activePackage.dwelling && this.activePackage.dwelling.virtualTourUrl) {
        return this.activePackage.dwelling.virtualTourUrl;
      }

      if (this.activeProperty && this.activeProperty.dwelling && this.activeProperty.dwelling.virtualTourUrl) {
        return this.activeProperty.dwelling.virtualTourUrl;
      }

      return "";
    },

    activeAssetRatio() {
      if (!this.activeImage) {
        return 16 / 9;
      }

      let width = 4;
      let height = 3;

      const img = new Image();

      img.onload;
      img.src = this.activeImage;

      if (img.width && img.height) {
        width = img.width;
        height = img.height;
      }

      return width / height;
    },

    hasDefaultPropertyLabel() {
      if (this.property.priceLabel === "Price" || this.property.priceLabel === "سعر") {
        return true;
      } else {
        return false;
      }
    },
  },

  mounted() {
    if (this.$store.state.navSlave) {
      this.$store.state.socket.on("galleryNavTo", (imageIndex) => {
        this.imageIndex = imageIndex;
      });
    }
  },

  watch: {
    imageIndex() {
      if (this.$store.state.navMaster) {
        this.$store.state.socket.emit("galleryNav", this.imageIndex);
      }
    },

    property(newProperty) {
      if (!isEmpty(newProperty)) {
        const assets = newProperty.images.map((image) => {
          return this.$store.state.assetsPath + image;
        });

        this.imageIndex = 0;

        this.$store.dispatch("announceSwarmContext", process.env.VUE_APP_PROJECT + "-" + newProperty.label.toLowerCase().replaceAll(" ", "-"));

        if (this.virtualTourUrl) {
          this.images = [...assets, "/assets/virtual-tour-slide.jpg"];
        } else {
          this.images = assets;
        }
      }
    },
  },

  methods: {
    addToFavourites(livingOption) {
      this.$store.commit("favourites/ADD_TO_FAVOURITES", livingOption._id);
    },

    closeModal() {
      this.imageIndex = 0;

      const routeName = this.$store.state.route.name;
      const category = this.$route.params.categoryId;

      if (routeName === "carousel") {
        this.$router.push(`${buildCarouselBasePath(this.$route)}${window.location.search}`);
      }

      if (routeName === "package-modal") {
        this.$router.push(`${buildPackagesPath(this.$route)}${window.location.search}`);
      }

      if (routeName === "dwelling-modal") {
        this.$router.push(`${buildDwellingsPath(this.$route)}${window.location.search}`);
      }

      if (routeName === "favourite-modal") {
        this.$router.push(`/category/${category}/favourites?p=${this.favouriteIds.toString()}`);
      }

      if (routeName === "live-display-homes-property") {
        this.$router.push(`${buildProductDisplayHomesPath(category)}${window.location.search}`);
      }

      if (isPropertyRoute(routeName)) {
        let currentPath = this.$route.path.split("property")[0];
        currentPath = currentPath.substr(0, currentPath.length - 1);
        this.$router.push(currentPath + `?activeSubsectionId=${this.activeSubsectionId}`);
      }
    },

    handleTouchStart(event) {
      console.log("touchstart");
      if (this.images.length <= 1) return;

      const touch = event.changedTouches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.didSwipe = false;
    },

    handleTouchMove(event) {
      console.log("touchmove");
      if (this.images.length <= 1) return;

      const touch = event.changedTouches[0];
      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    },

    handleTouchEnd() {
      console.log("touchend", this.touchDeltaX, this.touchDeltaY);
      if (this.images.length <= 1) return;

      const absX = Math.abs(this.touchDeltaX);
      const absY = Math.abs(this.touchDeltaY);

      const isHorizontalSwipe = absX > this.swipeThreshold && absY < this.swipeVerticalTolerance;

      if (!isHorizontalSwipe) {
        this.resetTouchState();
        return;
      }

      this.didSwipe = true;

      if (this.touchDeltaX < 0) {
        this.goToNextImage();
      } else {
        this.goToPrevImage();
      }

      this.resetTouchState();
    },

    resetTouchState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
    },

    goToNextImage() {
      if (this.imageIndex < this.images.length - 1) {
        this.imageIndex++;
      }
    },

    goToPrevImage() {
      if (this.imageIndex > 0) {
        this.imageIndex--;
      }
    },

    isFavourited(livingOptionId) {
      let isFavourited = false;
      this.favouriteIds.forEach((favouriteId) => {
        if (favouriteId === livingOptionId) {
          isFavourited = true;
        }
      });

      return isFavourited;
    },

    removeFromFavourites(livingOption) {
      this.$store.commit("favourites/REMOVE_FROM_FAVOURITES", livingOption._id);
    },

    viewPackages() {
      this.$router.push(`${buildProductPackagesPath(this.$route.params.categoryId)}?lotId=${this.property._id}`);
    },

    openVirtualTour() {
      if (this.didSwipe) {
        this.didSwipe = false;
        return;
      }

      if (this.virtualTourUrl) {
        this.$store.commit("property/SET_ACTIVE_VIRTUAL_TOUR_URL", this.virtualTourUrl);
      }
    },

    viewFacades() {
      this.$store.commit("actions/SET_SHOW_ASSET_PAYLOAD", { payload: this.facadeImages });
    },
  },
};
</script>

<style lang="scss">
.property-modal {
  bottom: 0;
  left: 0;
  pointer-events: auto;
  position: absolute;
  right: 32.5rem;
  top: 0;
  z-index: var(--modal-z-index);

  .nav-hidden & {
    right: 0;
  }

  .nav-button {
    // border: 0.2rem solid white;
    position: relative;

    &.inactive {
      .nav-button__ring {
        display: none;
      }
    }

    &__ring {
      border: 0.3rem solid #fff;
      border-radius: 3rem;
      height: 2.2rem;
      width: 2.2rem;
      position: absolute;
      animation: pulsate 3s ease-out;
      animation-iteration-count: infinite;
      opacity: 0;
      top: 1.7rem;
      left: 1.7rem;
    }
  }

  &__swipe-layer {
    position: absolute;
    inset: 0;
    z-index: 5;
    pointer-events: auto;
  }

  &__mask {
    background: rgba(0, 0, 0, 0.6);
    height: 100%;
    pointer-events: auto;
    position: absolute;
    width: 100%;
  }

  &__builder-logo {
    background: transparent;
    // border: 1px solid black;
    margin-bottom: 1rem;
    // padding: 1rem;
    // max-height: 10rem;
    // object-fit: contain;
    width: 100%;
  }

  &__inner {
    // border-radius: .5rem;
    top: 50%;
    display: flex;
    flex-direction: column;
    left: 50%;
    pointer-events: auto;
    position: absolute;
    transform: translate(-50%, calc(-50% - 5rem));
    width: 112rem;

    .nav-hidden & {
      transform: translate(-50%, -50%);
    }

    .has-features & {
      left: calc(50% - 15rem);
    }
  }

  &__buttons {
    align-items: center;
    display: flex;
    gap: 2rem;

    .nav-hidden & {
      display: none;
    }

    .btn {
      background: var(--button-background);
      border-radius: var(--border-radius);
      box-shadow: 2px 3px 4px rgba(0, 0, 0, 0.5);
      padding: var(--button-padding);
      line-height: 1;
      height: var(--button-height);
      font-size: var(--button-font-size);
      text-align: center;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      justify-content: center;

      .icon {
        margin-right: 1em;

        [dir="rtl"] & {
          margin-left: 1em;
          margin-right: 0;
        }
      }

      &.router-link-active {
        background: var(--button-background-active);
        color: var(--button-color-active);
      }

      &--favourites {
        background: var(--button-favourites-background);
        color: var(--button-favourites-color);

        > div {
          max-width: 9rem;
        }
      }

      &--favourites-remove {
        background: var(--button-warning-background);
        color: var(--button-warning-color);
      }
    }
  }

  &__note {
    bottom: 8rem;
    color: #000;
    right: 1rem;
    position: absolute;
    z-index: 2;

    &--top-right {
      bottom: unset;
      top: 1rem;
    }

    &--top-left {
      bottom: unset;
      left: 1rem;
      right: unset;
      top: 1rem;
    }

    &--bottom-left {
      left: 1rem;
      right: unset;
    }
  }

  &__status {
    border-radius: 50%;
    height: 12rem;
    left: 13.4rem;
    position: absolute;
    top: 4rem;
    width: 12rem;
    z-index: 1;

    &--on-hold {
      background: #f38433;
      filter: drop-shadow(0.8rem 0.8rem 0.75rem rgba(#000, 0.5));

      &::after {
        content: "ON HOLD";
        font-size: 2.7rem;
        left: 50%;
        letter-spacing: 0.15rem;
        line-height: 1;
        position: absolute;
        text-align: center;
        top: 2.8rem;
        transform: translateX(-50%);
      }

      &::before {
        background: #fff;
        content: "";
        left: 50%;
        line-height: 1;
        position: absolute;
        text-align: center;
        top: 9.2rem;
        transform: translateX(-50%);
        height: 0.2rem;
        width: 3.8rem;
      }
    }
  }

  &__gallery {
    aspect-ratio: 15 / 10;
    background: #fff;
    border-radius: var(--propertymodal-border-radius) var(--propertymodal-border-radius) 0 0;
    font-size: 0;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;
    aspect-ratio: 1500/1000;

    .has-features & {
      border-radius: var(--propertymodal-border-radius) 0 0;
    }

    .message-on-hold {
      color: #000;
      font-size: 1.4rem;
      font-weight: var(--font-weight-secondary);
      left: 50%;
      position: absolute;
      text-align: center;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 46rem;
    }

    img,
    video {
      background-color: #393939;
      height: 100%;
      object-fit: contain;
      width: 100%;
      aspect-ratio: 1500/1000;
    }
  }

  &__overlay-image {
    background-color: transparent !important;
    position: absolute;
    height: 100%;
    width: 100%;
    object-fit: contain;
    z-index: 999;
    pointer-events: none;
  }

  &__controls {
    align-items: center;
    bottom: 3rem;
    display: flex;
    justify-content: space-between;
    position: absolute;
    z-index: 10;
    right: 2rem;

    .nav-hidden & {
      display: none;
    }

    .prev {
      margin: 0 1rem 0 0;
    }

    .next {
      margin: 0 auto 0 0;
    }

    .btn-circle.inactive {
      filter: grayscale(50%);
      opacity: 0.25;
    }
  }

  &__features {
    background: var(--propertymodal-bg-color);
    border-radius: 0 var(--propertymodal-border-radius) var(--propertymodal-border-radius) 0;
    color: var(--propertymodal-font-color);
    flex-shrink: 0;
    padding: 4rem 2rem;
    overflow: scroll;
    pointer-events: all;
    position: relative;
    width: 30rem;
    height: 100%;
    right: -30rem;
    position: absolute;

    &::-webkit-scrollbar {
      appearance: none;
      height: 0;
      width: 0.75rem;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: var(--propertymodal-border-radius);
      background-color: rgba(0, 0, 0, 0.5);
    }

    .feature-heading {
      color: #000;
      font-size: 1.6rem;
      font-weight: var(--font-weight);
      margin: 0 0 0.6rem;
      opacity: 1;
      padding: 0;
      text-transform: uppercase;
    }

    .feature-list {
      color: #000;
      padding: 0 0 2rem;
      white-space: collapse;
      font-size: 1.2rem;

      ul {
        // list-style: none;
        padding: 0 1rem 0 1rem;
        margin: 0 0 2rem 0;

        li {
          line-height: 1;
          margin: 0.5em 0;
        }
      }
    }

    .feature {
      color: #000;
      font-size: 1.2rem;
      line-height: 1.3;
      padding: 0 0 2rem;
      word-wrap: break-word;
      // white-space: preserve;

      & ul {
        padding: 0 0 0 1em !important;
      }
    }
  }

  &__info {
    align-items: center;
    background: var(--propertymodal-info-bg-color);
    border-radius: 0 0 var(--propertymodal-border-radius) var(--propertymodal-border-radius);
    display: flex;
    color: var(--propertymodal-font-color);
    height: 7.5rem;
    overflow: hidden;
    padding: 2rem;

    .spacer {
      flex-grow: 1;
    }

    .has-features & {
      border-radius: 0 0 0 var(--propertymodal-border-radius);
    }

    .detail-wrapper {
      display: flex;
      // margin: 0 0 0 2rem;
      margin: 0;
      width: 100%;

      .details {
        display: flex;
        gap: 2rem;
      }
    }

    .detail {
      display: flex;
      flex-direction: column;
      line-height: 1;
      gap: 0.5rem;

      &__label {
        color: var(--propertymodal-label-color);
        font-size: 1rem;
        text-transform: uppercase;
        margin-bottom: 0.25rem;
        line-height: 1;
      }

      &__value {
        font-size: 1.4rem;
        position: relative;
        line-height: 1;
        text-transform: capitalize;

        sup {
          position: absolute;
          top: -0.2rem;
        }
      }
    }

    .facade {
      margin: auto 1rem auto auto;
    }

    .close {
      display: flex;
      align-items: center;
      margin: 0 2rem;

      .nav-hidden & {
        display: none;
      }
    }
  }

  &__dots {
    align-items: center;
    bottom: 2.2rem;
    display: flex;
    gap: 0.8rem;
    justify-content: center;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    z-index: 10;

    .nav-hidden & {
      display: none;
    }
  }

  &__dot {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    border: 0.1rem solid rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    box-sizing: border-box;
    display: block;
    flex: 0 0 0.8rem;
    height: 1rem;
    width: 1rem;
    min-width: 1rem;
    max-width: 1rem;
    min-height: 1rem;
    max-height: 1rem;
    padding: 0;
    margin: 0;
    line-height: 0;

    &--active {
      background: #fff;
      border-color: #fff;
    }
  }
}

.associated-packages {
  display: flex;
  align-items: center;

  .btn {
    align-items: center;
    display: flex;
    justify-content: center;
  }
}

.btn-circle {
  background: var(--button-background-color);
  color: var(--button-color);
  border-radius: 50%;
  box-shadow: var(--button-box-shadow);
  height: var(--button-height);
  width: var(--button-height);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}
</style>
